import React from 'react'
import { Card, CardMedia, Typography, Button, Box } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { Components, Product } from 'src/Libs/shop/models/system-configuration-components.model'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { getImageUrlBySize } from 'src/Libs/shop'
import { QuantityAvailabilityIndicator } from '../../ShopProduct'

interface MainComponentProductCardProps {
  productData: Product
  categoryId: string
  categoryData: Components
  isSelected: boolean
  onMoreInfo: (productId: string) => void
  onSelectProduct: (category: Components, product: Product) => void
}

const useStyles = makeStyles()(theme => ({
  product: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: '265px',
    maxWidth: '225px',
    flexShrink: 0,
    margin: '12px',
    padding: 0,
    paddingBottom: '12px',
    position: 'relative',
  },
  productActive: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    boxShadow: 'none',
  },
  activeLabel: {
    top: theme.spacing(2),
    left: theme.spacing(2),
    position: 'absolute',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderRadius: '24px',
  },
  productImage: {
    margin: 0,
    padding: 0,
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    marginBottom: '12px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  button: {
    fontSize: '16px',
    padding: '16px 0px',
  },
  priceContainer: {
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  containerTextWrapper: {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
}))

/*
 * Main Component Product Card Layout
 */
const MainComponentProductCard: React.FC<MainComponentProductCardProps> = ({
  productData,
  categoryData,
  isSelected,
  onMoreInfo,
  onSelectProduct,
}) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()

  const unitPrice = productData.calculatedPrice.unitPrice
  const price = productData.calculatedPrice.listPrice?.price ?? 0
  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const productAvailability = productData.extensions?.e3dcAvailability
  const isPriceApplied = productData.extensions?.e3dcPriceApplied.state

  return (
    <Card className={cx(classes.product, isSelected && classes.productActive)}>
      {isSelected && <Typography className={classes.activeLabel}>{t('shop.common.selected')}</Typography>}

      <Box>
        <CardMedia
          className={classes.productImage}
          component="img"
          image={productImageUrl}
          alt="Product"
          onClick={() => onMoreInfo(productData.id)}
        />

        <Box className={classes.containerTextWrapper}>
          <Box className={classes.priceContainer}>
            <Typography align="left" variant="h3">
              {productData.translated.name}
              <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box>
        <Box className={classes.containerTextWrapper}>
          <Box className={classes.flexPrice}>
            <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
              {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
            </Typography>

            {unitPrice < price && (
              <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                {currencyFormatter(price)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box className={classes.buttonContainer}>
          <Button className={classes.button} size="large" color="inherit" variant="outlined" fullWidth onClick={() => onMoreInfo(productData.id)}>
            {t('shop.common.more-info')}
          </Button>

          <Button
            className={classes.button}
            color={isSelected ? 'inherit' : 'primary'}
            disabled={!isPriceApplied}
            variant="contained"
            fullWidth
            onClick={() => onSelectProduct(categoryData, productData)}
          >
            {isSelected ? t('shop.common.remove') : t('shop.common.select')}
          </Button>
        </Box>
      </Box>
    </Card>
  )
}

export default MainComponentProductCard
